import { EntityDropdownInput } from "#src/batteries-included-components/Dropdowns/EntityDropdownInput";
import { useQuery } from "@tanstack/react-query";
import { DropdownInput, Form, useForm } from "@validereinc/common-components";
import {
  EventCategoryAdapter,
  EventSchemaAdapter,
  Resources,
} from "@validereinc/domain";
import { Events } from "@validereinc/domain-controllers";
import React, { useEffect } from "react";
import {
  CreateEventFormValuesType,
  useWorkflowConfigurationFormStore,
} from "../WorkflowConfigurationFormStore";

const { EventStatusOptions } = Events.view;

const WorkflowConfigurationEventDetails = () => {
  const { setCreateEvent, setCreateEventIsValid, getCreateEvent } =
    useWorkflowConfigurationFormStore();

  const storeValues = getCreateEvent();

  const handleValueChange = (
    key: "event_schema_id" | "event_status" | "event_category_id",
    value: unknown
  ) => {
    if (storeValues) {
      setCreateEvent({
        ...storeValues,
        [key]: value,
      });
    }
  };

  const form = useForm<CreateEventFormValuesType>({
    values: storeValues,
  });

  const { data: eventSchema, isLoading: isLoadingEventSchema } = useQuery(
    [Resources.EVENT_SCHEMA, storeValues?.event_schema_id],
    () => {
      if (!storeValues?.event_schema_id) {
        return;
      }

      return EventSchemaAdapter.getOne({
        id: storeValues.event_schema_id,
      }).then(({ data }) => data);
    },
    {
      enabled: Boolean(storeValues?.event_schema_id),
    }
  );

  const { data: eventSchemas, isLoading: isLoadingEventSchemas } = useQuery(
    [Resources.EVENT_SCHEMA, storeValues?.event_category_id],
    () => {
      if (!storeValues?.event_category_id) {
        return;
      }

      return EventSchemaAdapter.getList({
        filters: {
          ["event_category.id"]: storeValues.event_category_id,
        },
      });
    },
    { enabled: Boolean(storeValues?.event_category_id) }
  );

  useEffect(() => {
    setCreateEventIsValid(form.formState.isValid);
  }, [form.formState.isValid, setCreateEventIsValid]);

  useEffect(() => {
    if (eventSchema?.event_category_id) {
      handleValueChange("event_category_id", eventSchema.event_category_id);
    }
  }, [eventSchema]);

  const isLoading =
    (Boolean(storeValues?.event_schema_id) && isLoadingEventSchema) ||
    (Boolean(storeValues?.event_category_id) && isLoadingEventSchemas);

  return (
    <Form {...form}>
      <EntityDropdownInput
        fetchEntityList={EventCategoryAdapter.getList}
        label="Category"
        name="event_category_id"
        isMulti={false}
        sort={{
          sortBy: "name",
          sortDirection: "asc",
        }}
        onChange={(categoryId: string) => {
          if (storeValues) {
            setCreateEvent({
              ...storeValues,
              event_category_id: categoryId,
              event_schema_id: "",
            });
          }
        }}
        isFluid
        isLoading={isLoading}
      />
      <DropdownInput
        label="Template"
        name="event_schema_id"
        options={eventSchemas?.data ?? []}
        placeholder="Select Template..."
        isSortedAlphabetically
        valueKey="id"
        labelKey="name"
        isRequired
        isFluid
        isLoading={isLoading}
        onChange={(value: string) =>
          handleValueChange("event_schema_id", value)
        }
      />
      <DropdownInput
        label="Event Status"
        name="event_status"
        options={EventStatusOptions}
        placeholder="Select Event Status..."
        isSortedAlphabetically
        valueKey="value"
        labelKey="label"
        isRequired
        isFluid
        onChange={(value: string) => handleValueChange("event_status", value)}
      />
    </Form>
  );
};

export default WorkflowConfigurationEventDetails;
